import { useToast } from '@/hooks/useToast'
import { useEffect, useRef, useState } from 'react'
import { ArtistsResponseProps } from '@/types/artistsProps'
import { SceneDataProps } from '@/types/sceneProps'
import {
  EMPTY_PAGINATION_META,
  type PaginatedResponseProps,
  type PaginationMetaProps,
  normalizePaginationMeta,
} from '@/types/paginationProps'

import {
  IoHeart,
  IoMusicalNoteSharp,
  IoPerson,
  IoPlay,
  IoReload,
  IoTrash,
} from 'react-icons/io5'
import colors from 'tailwindcss/colors'
import { FormArtist } from '../FormArtist'
import { useModal } from '@/hooks/useModal'
import { api } from '@/services/api'
import { CatalogStatusBadge } from '../CatalogStatusBadge'
import { EmptyState } from '../EmptyState'
import { ARTISTS_UPDATED_EVENT } from '@/services/artistsEvents'
import { SCENES_UPDATED_EVENT } from '@/services/scenesEvents'
import { fetchScenesOptions } from '@/services/scenes'

interface ArtistMetricProps {
  icon: JSX.Element
  label: string
  value: number
}

function ArtistMetric({ icon, label, value }: ArtistMetricProps) {
  return (
    <div className="rounded-xl bg-gray-200/70 px-3 py-2 text-left">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {icon}
        <span>{label}</span>
      </div>
      <p className="mt-2 text-lg font-bold text-gray-700">{value}</p>
    </div>
  )
}

export function Artists() {
  const [titleFilter, setTitleFilter] = useState('')
  const [sceneOptions, setSceneOptions] = useState<SceneDataProps[]>([])
  const [selectedSceneSlug, setSelectedSceneSlug] = useState('')
  const [artistsList, setArtistsList] = useState<ArtistsResponseProps[]>([])
  const [paginationMeta, setPaginationMeta] = useState<PaginationMetaProps>(
    EMPTY_PAGINATION_META,
  )
  const [isLoadingInitial, setIsLoadingInitial] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isLoadingScenes, setIsLoadingScenes] = useState(false)

  const isLoadingMoreRef = useRef(false)
  const paginationMetaRef = useRef<PaginationMetaProps>(EMPTY_PAGINATION_META)
  const isSearchingRef = useRef(false)
  const titleFilterRef = useRef('')
  const selectedSceneSlugRef = useRef('')
  const canLoadMoreRef = useRef(false)

  const { openModal, closeModal } = useModal()
  const { showToast } = useToast()

  const isFiltering = titleFilter.trim().length >= 3
  const canLoadMore =
    paginationMeta.lastPage > 0 &&
    paginationMeta.currentPage < paginationMeta.lastPage

  const handleFetchArtistsPage = async (
    page: number,
    filter?: string,
    scene?: string,
  ) => {
    const response = await api.get<
      PaginatedResponseProps<ArtistsResponseProps>
    >('/artists', {
      params: {
        page,
        ...(filter ? { title: filter } : {}),
        ...(scene ? { scene } : {}),
      },
    })

    const pagination = normalizePaginationMeta(
      response.data.meta,
      response.data.data.length,
    )

    return {
      artists: response.data.data,
      pagination,
    }
  }

  const handleLoadFirstPage = async (filter?: string, scene?: string) => {
    try {
      setIsLoadingInitial(true)

      const response = await handleFetchArtistsPage(1, filter, scene)
      setArtistsList(response.artists)
      setPaginationMeta(response.pagination)
    } catch (error) {
      showToast('Error fetching artists', {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setIsLoadingInitial(false)
    }
  }

  const handleRefreshArtists = async () => {
    const currentFilter = titleFilterRef.current
    const currentScene = selectedSceneSlugRef.current

    try {
      setIsRefreshing(true)

      if (currentFilter.length >= 3) {
        const response = await handleFetchArtistsPage(
          1,
          currentFilter,
          currentScene || undefined,
        )
        setArtistsList(response.artists)
        setPaginationMeta(response.pagination)
        return
      }

      const response = await handleFetchArtistsPage(
        1,
        undefined,
        currentScene || undefined,
      )
      setArtistsList(response.artists)
      setPaginationMeta(response.pagination)
    } catch (error) {
      showToast('Error refreshing artists', {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleFilterArtists = async (filter: string) => {
    setTitleFilter(filter)
    const currentScene = selectedSceneSlugRef.current

    if (filter.trim().length < 3) {
      setIsSearching(false)
      await handleLoadFirstPage(undefined, currentScene || undefined)
      return
    }

    try {
      setIsSearching(true)
      const response = await handleFetchArtistsPage(
        1,
        filter.trim(),
        currentScene || undefined,
      )
      setArtistsList(response.artists)
      setPaginationMeta(response.pagination)
    } catch (error) {
      showToast('Error fetching artists', {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setIsSearching(false)
    }
  }

  const handleSelectScene = async (sceneSlug: string) => {
    setSelectedSceneSlug(sceneSlug)
    const currentFilter = titleFilterRef.current

    if (currentFilter.length >= 3) {
      setIsSearching(true)
    }

    try {
      const response = await handleFetchArtistsPage(
        1,
        currentFilter.length >= 3 ? currentFilter : undefined,
        sceneSlug || undefined,
      )
      setArtistsList(response.artists)
      setPaginationMeta(response.pagination)
    } catch (error) {
      showToast('Error filtering artists by scene', {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setIsSearching(false)
    }
  }

  const handleRemoveArtist = async (artist: ArtistsResponseProps) => {
    if (!artist.id) return

    try {
      await api.delete(`/artists/${artist.id}`)

      showToast('Artist removed successfully', {
        type: 'success',
        theme: 'light',
      })

      closeModal()

      setArtistsList((prev) => prev.filter((item) => item.id !== artist.id))
      setPaginationMeta((prev) => ({
        ...prev,
        totalItems: Math.max(0, prev.totalItems - 1),
      }))
    } catch (error) {
      showToast('Error removing artist', {
        type: 'error',
        theme: 'light',
      })
    }
  }

  useEffect(() => {
    handleLoadFirstPage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    paginationMetaRef.current = paginationMeta
    isSearchingRef.current = isSearching
    titleFilterRef.current = titleFilter.trim()
    selectedSceneSlugRef.current = selectedSceneSlug
    canLoadMoreRef.current = canLoadMore
  }, [canLoadMore, isSearching, paginationMeta, selectedSceneSlug, titleFilter])

  useEffect(() => {
    let isMounted = true

    const loadScenes = async () => {
      try {
        setIsLoadingScenes(true)
        const scenes = await fetchScenesOptions()

        if (!isMounted) return

        setSceneOptions(scenes)
      } catch (error) {
        if (!isMounted) return

        showToast('Error fetching scenes', {
          type: 'error',
          theme: 'light',
        })
      } finally {
        if (isMounted) {
          setIsLoadingScenes(false)
        }
      }
    }

    loadScenes()

    return () => {
      isMounted = false
    }
  }, [showToast])

  useEffect(() => {
    const loadMoreArtists = async () => {
      const nextPage = paginationMetaRef.current.currentPage + 1

      if (
        !canLoadMoreRef.current ||
        nextPage <= 0 ||
        isSearchingRef.current ||
        isLoadingMoreRef.current
      ) {
        return
      }

      try {
        isLoadingMoreRef.current = true
        setIsLoadingMore(true)

        const response = await handleFetchArtistsPage(
          nextPage,
          isFiltering ? titleFilterRef.current : undefined,
          selectedSceneSlugRef.current || undefined,
        )
        paginationMetaRef.current = response.pagination
        canLoadMoreRef.current =
          response.pagination.lastPage > 0 &&
          response.pagination.currentPage < response.pagination.lastPage

        setArtistsList((prev) => [...prev, ...response.artists])
        setPaginationMeta(response.pagination)
      } catch (error) {
        showToast('Error fetching more artists', {
          type: 'error',
          theme: 'light',
        })
      } finally {
        isLoadingMoreRef.current = false
        setIsLoadingMore(false)
      }
    }

    const handleScroll = () => {
      if (
        !canLoadMoreRef.current ||
        isSearchingRef.current ||
        isLoadingMoreRef.current
      ) {
        return
      }

      const viewportHeight = window.innerHeight
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      const fullHeight = document.documentElement.scrollHeight

      if (!fullHeight) {
        return
      }

      const scrollProgress = (scrollTop + viewportHeight) / fullHeight

      if (scrollProgress >= 0.8) {
        loadMoreArtists()
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [isFiltering, showToast])

  useEffect(() => {
    const handleArtistsUpdated = () => {
      handleRefreshArtists()
    }

    window.addEventListener(ARTISTS_UPDATED_EVENT, handleArtistsUpdated)

    return () => {
      window.removeEventListener(ARTISTS_UPDATED_EVENT, handleArtistsUpdated)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handleScenesUpdated = async () => {
      try {
        const scenes = await fetchScenesOptions()
        setSceneOptions(scenes)

        const currentSceneStillExists = scenes.some(
          (scene) => scene.slug === selectedSceneSlugRef.current,
        )

        if (!currentSceneStillExists && selectedSceneSlugRef.current) {
          setSelectedSceneSlug('')
          await handleRefreshArtists()
        }
      } catch (error) {
        showToast('Error refreshing scenes', {
          type: 'error',
          theme: 'light',
        })
      }
    }

    window.addEventListener(SCENES_UPDATED_EVENT, handleScenesUpdated)

    return () => {
      window.removeEventListener(SCENES_UPDATED_EVENT, handleScenesUpdated)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <div className="h-[1px] bg-gray-300/50 my-7" />
      <div className="mt-8">
        <h4 className="font-bold text-sm my-3">Search by artist</h4>
        <div className="flex max-w-[28rem] items-center gap-2">
          <input
            placeholder="Type an artist"
            type="text"
            onChange={(e) => {
              handleFilterArtists(e.currentTarget.value)
            }}
            className="w-full rounded border border-gray-300 bg-white px-4 py-2 text-sm font-normal leading-tight focus:border-gray-500 focus:bg-white focus:outline-none data-[is-error=true]:border-red-600"
          />
          <button
            type="button"
            title="Reload artists"
            onClick={handleRefreshArtists}
            disabled={isLoadingInitial || isSearching || isRefreshing}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded border border-gray-300 bg-white text-purple-600 transition hover:border-purple-300 hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <IoReload
              size={18}
              className={isRefreshing ? 'animate-spin' : undefined}
            />
          </button>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => handleSelectScene('')}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
              !selectedSceneSlug
                ? 'border-purple-600 bg-purple-600 text-white'
                : 'border-gray-300 bg-white text-gray-600 hover:border-purple-300 hover:text-purple-600'
            }`}
          >
            All scenes
          </button>

          {sceneOptions.map((scene) => (
            <button
              key={scene.id}
              type="button"
              onClick={() => handleSelectScene(scene.slug)}
              className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                selectedSceneSlug === scene.slug
                  ? 'border-purple-600 bg-purple-600 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:border-purple-300 hover:text-purple-600'
              }`}
            >
              {scene.title}
            </button>
          ))}

          {isLoadingScenes && (
            <span className="px-1 py-1.5 text-sm text-gray-500">
              Loading scenes...
            </span>
          )}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between gap-3 md:flex-col md:items-start">
        <p className="text-sm text-gray-500">
          Showing {artistsList.length} of {paginationMeta.totalItems} artists
        </p>

        {isFiltering && (
          <p className="text-sm text-gray-500">
            {isSearching ? 'Searching...' : `Filtered by "${titleFilter}"`}
          </p>
        )}
      </div>

      {artistsList.map((artist) => (
        <div
          key={artist.id}
          className="relative mt-6 w-full rounded-2xl border bg-white p-6 transition-colors hover:border-gray-300 base:p-5 md:p-4"
        >
          <button
            title="Remove"
            className="absolute -right-1.5 -top-1.5 z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-red-600 bg-red-600 shadow-sm transition hover:bg-red-700"
            onClick={() => {
              openModal({
                textConfirm: 'Delete',
                description: 'Do you really want to delete the artist?',
                title: 'Attention',

                confirm() {
                  handleRemoveArtist(artist)
                },
              })
            }}
          >
            <IoTrash size={14} color={colors.white} />
          </button>

          <div className="flex items-start justify-between gap-6 base:flex-col">
            <button
              className="flex min-w-0 flex-1 items-start gap-4 text-left md:flex-col md:gap-3"
              onClick={() => {
                openModal({
                  children: <FormArtist artist={artist} />,
                })
              }}
            >
              <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full bg-gray-700">
                {artist.photoURL ? (
                  <img
                    src={artist.photoURL}
                    alt="artist photo profile"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center">
                    <IoPerson size={30} color={colors.white} />
                  </div>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <p className="truncate text-lg font-bold text-purple-600">
                  {artist.title}
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  {artist.musicalGenres
                    .slice(0, 3)
                    .map((genre) => genre.title)
                    .join(', ') || 'No genre linked'}
                </p>

                <div className="mt-3 grid grid-cols-1 justify-items-start gap-2">
                  <CatalogStatusBadge
                    label="Discover"
                    value={artist.discoveryStatus}
                  />
                  <CatalogStatusBadge
                    label="Claim"
                    value={artist.profileClaimStatus}
                  />
                  <CatalogStatusBadge
                    label="Photo"
                    value={artist.visualAssetStatus}
                  />
                  <CatalogStatusBadge
                    label="Cover"
                    value={artist.coverAssetStatus}
                  />
                </div>

                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                  <span>{artist.bio ? 'Bio ready' : 'No bio'}</span>
                  <span>
                    {(artist.socialLinks?.length ?? 0) > 0
                      ? `${artist.socialLinks?.length ?? 0} links`
                      : 'No links'}
                  </span>
                </div>
              </div>
            </button>

            <div className="w-full max-w-[300px] shrink-0 base:max-w-none">
              <div className="grid grid-cols-1 gap-2 md:w-full">
                <ArtistMetric
                  icon={
                    <IoMusicalNoteSharp size={16} color={colors.purple[600]} />
                  }
                  label="Musics"
                  value={(artist.musics && artist.musics.length) ?? 0}
                />
                <ArtistMetric
                  icon={<IoHeart size={16} color={colors.red[600]} />}
                  label="Likes"
                  value={artist.like ?? 0}
                />
                <ArtistMetric
                  icon={<IoPlay size={16} color={colors.blue[600]} />}
                  label="Streams"
                  value={artist.view ?? 0}
                />
              </div>
            </div>
          </div>
        </div>
      ))}

      {artistsList.length === 0 && !isLoadingInitial && !isSearching && (
        <EmptyState
          title={isFiltering ? 'No artists found' : 'No artists yet'}
          description={
            isFiltering
              ? 'Try another search term to find an existing artist.'
              : 'Create the first artist to start managing the catalog.'
          }
        />
      )}

      {(isLoadingInitial || isLoadingMore) && (
        <div className="mt-8 flex items-center justify-center gap-3 text-sm text-gray-500">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-purple-200 border-t-purple-600" />
          <p>
            {isLoadingInitial
              ? 'Loading artists...'
              : 'Loading more artists...'}
          </p>
        </div>
      )}
    </>
  )
}
